import { z } from "zod";
import { eouProviderConfig } from "@/models/eou";
import { llmProviderConfig } from "@/models/llm";
import { sttProviderConfig } from "@/models/stt";
import { ttsProviderConfig } from "@/models/tts";
import { vadProviderConfig } from "@/models/vad";
import { deepClone } from "@/shared/deep-clone";
import { deepMerge } from "@/shared/deep-merge";
import * as op from "@/shared/operation";
import { zodObjectWithTelemetry } from "@/telemetry/helpers/zod";
import type { TelemetryConsumer } from "@/telemetry/types";
import { transportNodeConfig } from "@/transport/config/node";
import { agentClientConfig } from "../client/config";

export const agentServerConfig = zodObjectWithTelemetry({
  schema: z.object({
    transport: transportNodeConfig.schema.prefault({ provider: "livekit" }),
    models: z
      .object({
        vad: vadProviderConfig.schema.prefault({ provider: "silero" }),
        stt: sttProviderConfig.schema.prefault({ provider: "deepgram" }),
        eou: eouProviderConfig.schema.prefault({ provider: "livekit" }),
        llm: llmProviderConfig.schema.prefault({ provider: "openai" }),
        tts: ttsProviderConfig.schema.prefault({ provider: "cartesia" }),
      })
      .prefault({}),
    telemetry: z
      .object({
        consumers: z.array(z.custom<TelemetryConsumer>()).prefault([]),
      })
      .prefault({}),
    experimental: z.object().prefault({}),
  }),
  toTelemetry: (config) => {
    config.transport = transportNodeConfig.toTelemetry(config.transport) as never;
    config.models.vad = vadProviderConfig.toTelemetry(config.models.vad) as never;
    config.models.stt = sttProviderConfig.toTelemetry(config.models.stt) as never;
    config.models.eou = eouProviderConfig.toTelemetry(config.models.eou) as never;
    config.models.llm = llmProviderConfig.toTelemetry(config.models.llm) as never;
    config.models.tts = ttsProviderConfig.toTelemetry(config.models.tts) as never;

    // Remember if telemetry consumers are defined and redact them (non-serializable)
    config.telemetry.hasCustomConsumers = Boolean(config.telemetry.consumers.length);
    config.telemetry.consumers = "redacted" as never;

    return config;
  },
});

/**
 * Used to define a global config in a `life.config.ts` file.
 * @param def - The config definition.
 * @returns The validated config.
 */
export function defineConfig(config: z.input<typeof agentServerConfig.schema>) {
  return config;
}

export function prepareAgentConfig(
  agentConfig: z.input<typeof agentServerConfig.schema>,
  globalConfigs: z.input<typeof agentServerConfig.schema>[],
) {
  // Obtain and validate the final config object
  const orderedGlobalConfigs = deepClone(globalConfigs).reverse();
  const mergedConfig = deepMerge(...orderedGlobalConfigs, agentConfig);
  const { error: errorConfig, data: serverConfig } =
    agentServerConfig.schema.safeParse(mergedConfig);
  if (errorConfig)
    return op.failure({
      code: "Validation",
      message: "Invalid agent server config.",
      cause: errorConfig,
    });

  // Produce the client-side subset config
  const { error: errorClientConfig, data: clientConfig } =
    agentClientConfig.schema.safeParse(serverConfig);
  if (errorClientConfig)
    return op.failure({
      code: "Validation",
      message: "Invalid agent client config.",
      cause: errorClientConfig,
    });

  return op.success({ server: serverConfig, client: clientConfig });
}
